import { normalizeImagePosition } from "../landingStudio/segmentVisualStyle";

export const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  green: "#26A269",
  blue: "#2878D4",
  yellow: "#FFC857",
};


export function buildDefaultDesignTokens(theme = {}) {
  const borderRadius = Number.isFinite(Number(theme.borderRadius))
    ? Number(theme.borderRadius)
    : 10;
  const divider = theme.dividerColor || "rgba(26,26,46,.09)";

  return {
    colors: {
      primary: theme.primaryColor || "#F35A3A",
      secondary: theme.secondaryColor || "#19242F",
      background: theme.backgroundColor || "#EEF0F5",
      surface: theme.surfaceColor || "#FFFFFF",
      text: theme.textColor || "#1A1A2E",
      mutedText: theme.mutedTextColor || "#5B5D73",
      divider,
      onPrimary: "#FFFFFF",
      onSecondary: "#FFFFFF",
      success: "#2E7D32",
      warning: "#ED6C02",
      error: "#D32F2F",
    },
    typography: {
      fontFamily: theme.fontFamily || "Asap",
      headingFontFamily: theme.headingFontFamily || "Exo 2",
      bodyFontFamily: theme.bodyFontFamily || "Asap",
      baseFontSize: 16,
      scaleRatio: 1.2,
      lineHeight: 1.5,
      headingLineHeight: 1.15,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 800,
      letterSpacing: 0,
    },
    spacing: {
      unit: 8,
      xs: 4,
      sm: 8,
      md: 16,
      lg: 24,
      xl: 32,
      xxl: 48,
      section: 72,
    },
    borders: {
      width: 1,
      style: "solid",
      radiusSm: Math.max(2, Math.round(borderRadius / 2)),
      radiusMd: borderRadius,
      radiusLg: Math.min(160, borderRadius + 8),
      radiusPill: 9999,
      color: divider,
    },
    shadows: {
      sm: "0 1px 3px rgba(26,26,46,.10)",
      md: "0 8px 24px rgba(26,26,46,.12)",
      lg: "0 18px 48px rgba(26,26,46,.16)",
      focus: "0 0 0 3px rgba(243,90,58,.24)",
    },
  };
}

export const emptySettings = {
  schemaVersion: 11,
  publicStatus: "draft",
  revision: 1,
  business: {
    name: "Mi negocio",
    shortDescription: "",
    whatsapp: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    country: "Argentina",
    schedule: "",
    mapEmbedUrl: "",
    socials: {
      instagram: "",
      facebook: "",
      tiktok: "",
      linkedin: "",
      youtube: "",
      website: "",
    },
  },
  navigation: {
    style: "style-1",
    homeUrl: "/",
    showAboutLink: true,
    aboutLabel: "Sobre nosotros",
    aboutUrl: "#about",
    showHighlightsLink: true,
    highlightsLabel: "Por qué elegirnos",
    highlightsUrl: "#highlights",
    productsUrl: "/productos",
    contactUrl: "/contact",
    loginUrl: "/login",
    showProductsLink: true,
    showContactLink: true,
    showCart: true,
    showCustomerAccess: true,
    showSocialLinks: true,
    showLogo: true,
    homeLabel: "Inicio",
    productsLabel: "Productos",
    allProductsLabel: "Ver todos",
    megaMenuStyle: "auto",
    megaMenuContent: "auto",
    megaMenuDisplay: "both",
    megaMenuHierarchy: "category-first",
    showMegaMenuIntro: false,
    showMegaMenuStats: false,
    showMegaMenuLatest: false,
    megaMenuEyebrow: "",
    megaMenuTitle: "",
    megaMenuDescription: "",
    megaMenuCategoriesSubtitle: "",
    megaMenuSortLabel: "",
    megaMenuLatestLabel: "Nuevos",
    contactLabel: "Contacto",
    loginLabel: "Iniciar sesión",
    accountLabel: "Mi cuenta",
    logoutLabel: "Cerrar sesión",
    cartLabel: "Ver carrito",
    cartTooltip: "Carrito",
    socialTitle: "Redes sociales",
    loadingCategoriesText: "Cargando categorías…",
    emptyCategoriesText: "No hay categorías todavía.",
    categoriesErrorText: "No se pudieron cargar las categorías.",
    menuAriaLabel: "Abrir menú",
    closeMenuAriaLabel: "Cerrar menú",
    cartAriaLabel: "Abrir carrito",
    buttons: [],
  },
  hero: {
    eyebrow: "",
    title: "Mi negocio",
    subtitle: "",
    primaryCtaLabel: "Consultar",
    primaryCtaUrl: "/contact",
    primaryCtaRequiresProducts: false,
    primaryCtaShowIcon: true,
    primaryCtaIcon: "explore",
    secondaryCtaLabel: "Ver productos",
    secondaryCtaUrl: "/productos",
    secondaryCtaRequiresProducts: true,
    secondaryCtaShowIcon: true,
    secondaryCtaIcon: "storefront",
    imageUrl: "",
    slides: [],
    rotatingPhrases: [],
    overlay: "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))",
    imagePosition: "center",
    contentHorizontalAlign: "left",
    contentVerticalAlign: "center",
  },
  catalog: {
    mode: "auto",
    productsPage: {
      eyebrow: "",
      title: "",
      productsTitle: "Productos",
      servicesTitle: "Servicios",
      hybridTitle: "Productos + servicio",
      subtitle: "",
      loadingText: "Cargando catálogo…",
      resultSingular: "resultado",
      resultPlural: "resultados",
      searchPlaceholder: "Buscar por nombre, servicio, código, categoría o subcategoría",
      showSearch: true,
      showFilters: true,
      showCategoryFilter: true,
      showSubcategoryFilter: true,
      showTypeFilter: true,
      filtersLabel: "Filtros",
      typeFilterLabel: "Tipo",
      categoryFilterLabel: "Categoría",
      subcategoryFilterLabel: "Subcategoría",
      allTypesLabel: "Productos y servicios",
      allCategoriesLabel: "Todas las categorías",
      allSubcategoriesLabel: "",
      resetLabel: "Limpiar filtros",
      emptyTitle: "No encontramos resultados",
      emptyText: "Probá con otra búsqueda o quitá los filtros activos.",
      emptyResetLabel: "Ver todo el catálogo",
      applyFiltersLabel: "Aplicar filtros",
      loadMoreLabel: "Cargar más",
      paginationMode: "all",
      pageSize: 24,
      sortBy: "createdAt",
      sortDir: "desc",
      backgroundColor: "",
      surfaceColor: "",
      textColor: "",
      mutedTextColor: "",
      headerBackgroundColor: "",
      headerTextColor: "",
    },
    productPage: {
      backLabel: "Volver al catálogo",
      homeLabel: "",
      catalogLabel: "",
      additionalInfoTitle: "Información adicional",
      showBreadcrumbs: true,
      showStock: true,
      showAttributes: true,
      showCapabilities: true,
      showQuickCart: true,
      showWhatsapp: true,
      descriptionCollapsedLines: 0,
      descriptionMoreLabel: "Ver más",
      descriptionLessLabel: "Ver menos",
      layout: "split",
      imageFit: "contain",
      backgroundColor: "",
      surfaceColor: "",
      textColor: "",
      mutedTextColor: "",
    },
    cards: {
      gap: 18,
      radius: 16,
      backgroundColor: "",
      backgroundOpacity: 1,
      backgroundImageUrl: "",
      backgroundImagePosition: "center",
      backgroundImageFit: "cover",
      backgroundImageOpacity: 0.28,
      imagePosition: "top",
      imageFit: "cover",
      imageHeight: 210,
      showImage: true,
      showTitle: true,
      showButton: true,
      showType: true,
      showCategory: true,
      showSubcategory: true,
      showDescription: true,
      showPrice: true,
      showDuration: true,
      showCapabilities: true,
      showQuickCart: true,
      borderColor: "",
      shadowColor: "",
      typeColor: "",
      titleColor: "",
      categoryColor: "",
      subcategoryColor: "",
      descriptionColor: "",
      priceColor: "",
      buttonTextColor: "",
      buttonBackgroundColor: "",
      buttonHoverTextColor: "",
      buttonHoverBackgroundColor: "",
      ctaLabel: "Ver detalle",
      serviceCtaLabel: "Ver y reservar",
      ctaBehavior: "detail",
      ctaUrl: "",
    },
    brands: {
      eyebrow: "",
      title: "Marcas",
      subtitle: "Elegí una marca y explorá el catálogo disponible.",
      loadingText: "Cargando marcas…",
      emptyText: "Todavía no hay marcas para mostrar.",
      errorText: "No se pudieron cargar las marcas.",
      ctaLabel: "Ver productos",
      items: [],
    },
    spotlight: {
      enabled: false,
      eyebrow: "",
      title: "",
      description: "",
      logoUrl: "",
      logoAlt: "",
      backgroundImageUrl: "",
      backgroundImagePosition: "center",
      ctaLabel: "",
      ctaUrl: "",
      ctaRequiresProducts: false,
      primaryColor: "",
      secondaryColor: "",
    },
    featured: {
      eyebrow: "",
      title: "Productos destacados",
      subtitle: "Conocé las últimas novedades del catálogo.",
      loadingText: "Cargando productos…",
      emptyText: "Todavía no hay productos para mostrar.",
      errorText: "No se pudieron cargar los productos.",
      ctaLabel: "Ver detalle",
    },
    categories: {
      eyebrow: "",
      title: "Explorá por categorías",
      subtitle: "",
      loadingText: "Cargando categorías…",
      emptyText: "Todavía no hay categorías para mostrar.",
      errorText: "No se pudieron cargar las categorías.",
      ctaLabel: "Ver productos",
      productSingular: "producto",
      productPlural: "productos",
    },
  },
  about: {
    eyebrow: "Sobre nuestro negocio",
    title: "Conocé nuestro negocio",
    description: "Trabajamos para ofrecer una atención cercana, soluciones claras y una experiencia confiable en cada consulta.",
    imageUrl: "",
    imageAlt: "Conocé nuestro negocio",
    layout: "image-left",
    imagePosition: "center",
    ctaLabel: "Contactanos",
    ctaUrl: "#contact",
    ctaRequiresProducts: false,
  },
  highlightsIntro: {
    eyebrow: "Por qué elegirnos",
    title: "Una experiencia pensada para vos",
    subtitle: "Combinamos atención, calidad y agilidad para que cada contacto con el negocio sea simple y confiable.",
  },
  highlights: [
    {
      id: "atencion-personalizada",
      icon: "",
      title: "Atención personalizada",
      description: "Escuchamos cada necesidad y acompañamos al cliente de forma cercana.",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
    },
    {
      id: "calidad-y-confianza",
      icon: "",
      title: "Calidad y confianza",
      description: "Cuidamos cada detalle para brindar una experiencia consistente y segura.",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
    },
    {
      id: "respuesta-agil",
      icon: "",
      title: "Respuesta ágil",
      description: "Facilitamos la consulta y damos respuestas claras para avanzar sin vueltas.",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
    },
  ],
  contact: {
    title: "Contacto",
    subtitle: "",
    whatsappMessage: "Hola, quiero hacer una consulta.",
    backgroundImageUrl: "",
    backgroundImagePosition: "center",
    detailsCtaLabel: "Ver contacto",
    detailsCtaUrl: "/contact",
    whatsappCtaLabel: "Escribir por WhatsApp",
    floatingWhatsappAriaLabel: "Contactar por WhatsApp",
    mapTitle: "Ubicación",
  },
  footer: {
    style: "style-1",
    showLogo: true,
    logoPosition: "center",
    tagline: "",
    copyright: "{businessName}. Todos los derechos reservados.",
    navigationTitle: "Navegación",
    contactTitle: "Contacto",
    homeLabel: "Inicio",
    productsLabel: "Productos",
    contactLabel: "Contacto",
  },
  messages: {
    maintenanceTitle: "Estamos actualizando el sitio",
    maintenanceDescription: "{businessName} se encuentra temporalmente en mantenimiento.",
    unavailableTitle: "Sitio no disponible",
    unavailableDescription: "La presencia pública de este proyecto todavía no está habilitada.",
    catalogDisabledTitle: "Catálogo no disponible",
    catalogDisabledDescription: "El catálogo no está habilitado para este sitio.",
    ordersDisabledTitle: "Pedidos no disponibles",
    ordersDisabledDescription: "Podés consultar el catálogo y comunicarte directamente con el comercio.",
    ecommerceDisabledTitle: "Pago online no disponible",
    ecommerceDisabledDescription: "El pedido puede coordinarse directamente con el comercio.",
  },
  appearance: {
    about: { backgroundColor: "", surfaceColor: "", accentColor: "", titleColor: "", textColor: "" },
    highlights: {
      backgroundColor: "",
      backgroundImageUrl: "",
      backgroundImagePosition: "center",
      backgroundImageOpacity: 0.24,
      surfaceColor: "",
      accentColor: "",
      titleColor: "",
      textColor: "",
    },
    contact: { backgroundColor: "", surfaceColor: "", accentColor: "", titleColor: "", textColor: "" },
  },
  theme: {
    template: "catalog-basic",
    primaryColor: "#F35A3A",
    secondaryColor: "#19242F",
    surfaceColor: "#F7F8FC",
    backgroundColor: "#EEF0F5",
    textColor: "#1A1A2E",
    mutedTextColor: "#5B5D73",
    dividerColor: "rgba(26,26,46,.09)",
    logoUrl: "",
    compactLogoUrl: "",
    faviconUrl: "",
    fontFamily: "Asap",
    headingFontFamily: "Exo 2",
    bodyFontFamily: "Asap",
    googleFontsUrl: "",
    fonts: [],
    borderRadius: 10,
    mode: "light",
  },
  designTokens: buildDefaultDesignTokens({
    primaryColor: "#F35A3A",
    secondaryColor: "#19242F",
    surfaceColor: "#F7F8FC",
    backgroundColor: "#EEF0F5",
    textColor: "#1A1A2E",
    mutedTextColor: "#5B5D73",
    dividerColor: "rgba(26,26,46,.09)",
    fontFamily: "Asap",
    headingFontFamily: "Exo 2",
    bodyFontFamily: "Asap",
    borderRadius: 10,
  }),
  seo: { title: "", description: "", imageUrl: "" },
  sections: {
    showNavbar: true,
    showHero: true,
    showSpotlight: true,
    showBrands: true,
    showProducts: true,
    showFeatured: true,
    showCategories: true,
    showAbout: true,
    showHighlights: true,
    showContact: true,
    showFooter: true,
    showFloatingWhatsapp: true,
  },
  layout: {
    segments: [
      { id: "navbar", type: "navbar" },
      { id: "hero", type: "hero" },
      { id: "spotlight", type: "spotlight" },
      { id: "featured", type: "featured" },
      { id: "brands", type: "brands" },
      { id: "categories", type: "categories" },
      { id: "about", type: "about" },
      { id: "highlights", type: "highlights" },
      { id: "contact", type: "contact" },
      { id: "footer", type: "footer" },
    ],
  },
  updatedAt: null,
};

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function deepMerge(base, source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return source === undefined ? clone(base) : clone(source);
  }

  const output = clone(base);
  Object.entries(source).forEach(([key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      base?.[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      output[key] = deepMerge(base[key], value);
    } else if (value !== undefined) {
      output[key] = clone(value);
    }
  });
  return output;
}

function pickSettings(payload = {}) {
  const root = payload?.data?.data || payload?.data || payload || {};
  return root?.settings || root?.landing || root || {};
}

export function mergeSettings(payload = {}) {
  const source = pickSettings(payload);
  const merged = deepMerge(emptySettings, source);

  merged.schemaVersion = 11;
  merged.revision = Number(source?.revision || merged.revision || 1);
  merged.hero.imagePosition = normalizeImagePosition(merged.hero?.imagePosition);
  merged.hero.slides = Array.isArray(merged.hero?.slides)
    ? merged.hero.slides
        .filter((slide) => slide?.url)
        .map((slide) => ({
          ...slide,
          position: normalizeImagePosition(slide?.position, merged.hero.imagePosition),
        }))
    : [];
  merged.hero.rotatingPhrases = Array.isArray(merged.hero?.rotatingPhrases)
    ? merged.hero.rotatingPhrases.filter(Boolean)
    : [];
  merged.catalog.brands.items = Array.isArray(merged.catalog?.brands?.items)
    ? merged.catalog.brands.items.filter((item) => (item?.name ?? item?.label) || item?.logoUrl)
    : [];
  merged.designTokens = deepMerge(
    buildDefaultDesignTokens(merged.theme),
    source?.designTokens || {}
  );
  merged.designTokens.colors = {
    ...merged.designTokens.colors,
    primary: merged.theme.primaryColor,
    secondary: merged.theme.secondaryColor,
    background: merged.theme.backgroundColor,
    surface: merged.theme.surfaceColor,
    text: merged.theme.textColor,
    mutedText: merged.theme.mutedTextColor,
    divider: merged.theme.dividerColor,
  };
  merged.about.imagePosition = normalizeImagePosition(merged.about?.imagePosition);
  merged.catalog.spotlight.backgroundImagePosition = normalizeImagePosition(
    merged.catalog?.spotlight?.backgroundImagePosition
  );
  merged.contact.backgroundImagePosition = normalizeImagePosition(
    merged.contact?.backgroundImagePosition
  );
  merged.appearance.highlights.backgroundImagePosition = normalizeImagePosition(
    merged.appearance?.highlights?.backgroundImagePosition
  );
  const highlightsBackgroundOpacity = Number(merged.appearance?.highlights?.backgroundImageOpacity);
  merged.appearance.highlights.backgroundImageOpacity = Number.isFinite(highlightsBackgroundOpacity)
    ? Math.max(0, Math.min(1, highlightsBackgroundOpacity))
    : 0.24;
  merged.footer.logoPosition = normalizeImagePosition(merged.footer?.logoPosition);
  merged.highlights = Array.isArray(merged.highlights)
    ? merged.highlights
        .filter((item) => item?.title || item?.description || item?.imageUrl || item?.youtubeUrl)
        .map((item) => ({
          ...item,
          imagePosition: normalizeImagePosition(item?.imagePosition),
        }))
    : clone(emptySettings.highlights);

  // Una colección explícitamente vacía representa contenido eliminado. Sólo
  // una propiedad ausente hereda los valores iniciales mediante deepMerge.
  return merged;
}
